import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

/**
 * $33 BUILD YOUR OWN — POST-PURCHASE PAGE — /build-your-own-success?session_id=...
 *
 * Verifies the Stripe Checkout Session server-side, then reveals:
 *   1. A JWT-signed 7-day PDF download link (the printable pages).
 *   2. The PERMANENT practice URL — shown as visible, copyable text (survives
 *      a screenshot) AND as a button. It does not expire.
 * Fires GA4 + Meta Purchase ONCE on verify success, zero PII. noindex,nofollow.
 */

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  gold: "#F4C36A",
  goldText: "#1B6B6E",
  cream: "#efeae7",
  mint: "#5EEAD4",
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_RETRIES = 8;

export default function BuildYourOwnSuccess() {
  const [state, setState] = useState("verifying"); // verifying | ready | error
  const [downloadUrl, setDownloadUrl] = useState("");
  const [practiceUrl, setPracticeUrl] = useState("");
  const [email, setEmail] = useState("");
  const [txnId, setTxnId] = useState("");
  const [copied, setCopied] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    document.title = "It is yours · Build Your Own · Temple Franklin";
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const firePurchase = (value, transactionId) => {
      if (firedRef.current) return;
      const dedupeKey = `purchase_fired_${transactionId}`;
      try {
        if (window.sessionStorage && sessionStorage.getItem(dedupeKey)) {
          firedRef.current = true;
          return;
        }
      } catch (_) { /* storage guard */ }
      firedRef.current = true;
      const v = value && value > 0 ? value : 33.0;
      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", "purchase", {
            transaction_id: transactionId,
            value: v,
            currency: "USD",
            items: [{ item_id: "byo_mbsh", item_name: "Build Your Own: Mind Body Spirit HYGIENE", price: v, quantity: 1 }],
          });
        }
        if (typeof window.fbq === "function") {
          window.fbq("track", "Purchase", {
            value: v,
            currency: "USD",
            content_ids: ["byo_mbsh"],
            content_name: "Build Your Own: Mind Body Spirit HYGIENE",
            content_type: "product",
          });
        }
        if (typeof window.pintrk === "function") {
          window.pintrk("track", "checkout", {
            value: v,
            order_quantity: 1,
            currency: "USD",
            line_items: [{ product_name: "Build Your Own: Mind Body Spirit HYGIENE", product_id: "byo_mbsh", product_price: v }],
          });
        }
      } catch (_) { /* analytics guard */ }
      try {
        if (window.sessionStorage) sessionStorage.setItem(dedupeKey, "1");
      } catch (_) { /* storage guard */ }
    };

    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/pdf/verify-session`, {
          params: { session_id: sessionId },
        });
        if (cancelled) return;
        if (res.data && res.data.download_url) {
          setDownloadUrl(`${API_URL}${res.data.download_url}`);
          if (res.data.practice_path) {
            setPracticeUrl(`${window.location.origin}${res.data.practice_path}`);
          }
          setEmail(res.data.email || "");
          setTxnId(res.data.transaction_id || sessionId);
          firePurchase(res.data.value, res.data.transaction_id || sessionId);
          setState("ready");
          return;
        }
      } catch (_) { /* retry below */ }
      attempts += 1;
      if (attempts >= MAX_RETRIES) {
        if (!cancelled) setState("error");
        return;
      }
      setTimeout(poll, 2000);
    };

    poll();

    return () => {
      cancelled = true;
      const stale = document.querySelector('meta[name="robots"]');
      if (stale) stale.remove();
    };
  }, []);

  const copyPractice = async () => {
    try {
      await navigator.clipboard.writeText(practiceUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) { /* clipboard guard */ }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{ background: `linear-gradient(160deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`, color: BRAND.cream }}
      data-testid="byo-success-page"
    >
      <div className="max-w-2xl w-full text-center">
        <p className="font-sans text-xs tracking-[0.4em] uppercase mb-5" style={{ color: BRAND.gold }}>
          Mind Body Spirit HYGIENE™ ✦
        </p>

        {state === "verifying" && (
          <div data-testid="byo-success-verifying">
            <h1 className="font-serif tracking-tight leading-[1.05] mb-6" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
              Confirming your payment…
            </h1>
            <div
              className="inline-block w-10 h-10 rounded-full animate-spin mt-2"
              style={{ border: `3px solid ${BRAND.mint}33`, borderTopColor: BRAND.mint }}
              aria-label="Loading"
            />
            <p className="font-serif italic text-lg mt-8" style={{ color: `${BRAND.cream}cc` }}>
              This takes just a moment — hang tight.
            </p>
          </div>
        )}

        {state === "ready" && (
          <div data-testid="byo-success-ready">
            <h1
              className="font-serif tracking-tight leading-[1.05] mb-6"
              style={{ fontSize: "clamp(2.4rem, 5vw, 3.8rem)" }}
              data-testid="byo-success-headline"
            >
              It is <span style={{ color: BRAND.mint }} className="italic">yours.</span>
            </h1>

            <p className="font-serif text-base md:text-lg leading-relaxed mb-10 max-w-xl mx-auto" style={{ color: `${BRAND.cream}dd` }} data-testid="byo-success-body">
              Two things below. <strong style={{ color: BRAND.mint }}>Save the practice link now</strong> — bookmark it, or send it to
              yourself. It is how you get back in, and it does not expire. The PDF link is good for 7 days, so download it while you are here.
            </p>

            {/* THE PRACTICE — permanent, shown as visible copyable text + button */}
            <div
              className="max-w-xl mx-auto mb-8 rounded-2xl px-6 py-7 text-left"
              style={{ background: `${BRAND.mint}14`, border: `1px solid ${BRAND.mint}33` }}
              data-testid="byo-practice-section"
            >
              <p className="font-sans text-xs tracking-[0.3em] uppercase font-semibold mb-3 text-center" style={{ color: BRAND.mint }}>
                Your permanent practice link
              </p>
              <div
                className="rounded-lg px-4 py-3 mb-4 font-mono text-sm break-all select-all text-center"
                style={{ background: `${BRAND.deep}88`, color: BRAND.cream, border: `1px dashed ${BRAND.mint}55` }}
                data-testid="byo-practice-url-text"
              >
                {practiceUrl}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={practiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5"
                  style={{ background: BRAND.mint, color: BRAND.deep, fontSize: "15px" }}
                  data-testid="byo-open-practice-btn"
                >
                  Open the practice →
                </a>
                <button
                  onClick={copyPractice}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5"
                  style={{ background: "transparent", color: BRAND.mint, border: `1px solid ${BRAND.mint}` }}
                  data-testid="byo-copy-practice-btn"
                >
                  {copied ? "Copied ✓" : "Copy link"}
                </button>
              </div>
            </div>

            {/* THE PRINTABLE PAGES — 7-day PDF */}
            <div className="flex justify-center mb-4">
              <a
                href={downloadUrl}
                download
                className="inline-flex items-center gap-3 px-9 py-5 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5 hover:shadow-2xl"
                style={{ background: BRAND.gold, color: BRAND.goldText, fontSize: "17px" }}
                data-testid="byo-download-btn"
              >
                Download the printable pages (PDF)
                <span style={{ fontSize: "20px" }}>↓</span>
              </a>
            </div>
            <p className="font-serif italic text-sm mb-8" style={{ color: `${BRAND.cream}bb` }}>
              This PDF link is valid for 7 days — download it now while you're here.
            </p>

            <p className="font-sans text-xs tracking-wide" style={{ color: `${BRAND.cream}88` }} data-testid="byo-success-txn">
              Purchased from Mind Body Spirit HYGIENE™ · Transaction {txnId ? txnId.slice(-8) : "—"}
            </p>
          </div>
        )}

        {state === "error" && (
          <div data-testid="byo-success-error">
            <h1 className="font-serif tracking-tight leading-[1.05] mb-6" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
              We're still confirming your payment.
            </h1>
            <p className="font-serif italic text-lg leading-relaxed mb-8 max-w-xl mx-auto" style={{ color: `${BRAND.cream}dd` }}>
              Your payment may still be processing. Refresh this page in a moment — or email{" "}
              <a href="mailto:mindbodyspirithygiene@gmail.com" className="underline underline-offset-4" style={{ color: BRAND.mint }}>
                mindbodyspirithygiene@gmail.com
              </a>{" "}
              and Temple will send everything straight to you.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-sans font-semibold tracking-wide transition-transform hover:-translate-y-0.5"
              style={{ background: BRAND.gold, color: BRAND.goldText, fontSize: "15px" }}
              data-testid="byo-success-retry-btn"
            >
              Try again
            </button>
          </div>
        )}

        <a
          href="/"
          className="inline-block mt-10 font-sans text-sm tracking-wide underline-offset-4 hover:underline"
          style={{ color: `${BRAND.cream}aa` }}
          data-testid="byo-success-home-link"
        >
          ← Back to mindbodyspirithygienetools.com
        </a>
      </div>
    </div>
  );
}
